import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Loader2, Plus, Check, X, ChevronUp, ChevronDown, Save, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from '@/components/ui/sonner';
import { orderCycleAPI, adminAPI } from '@/lib/api';

const monthLabel = (m) => `${m.month_name.toUpperCase()}${m.year !== new Date().getFullYear() ? ` ${m.year}` : ''}`;

function VendorPicker({ suppliers, selectedIds, onAdd }) {
  const [open, setOpen] = useState(false);
  const available = suppliers.filter(s => !selectedIds.includes(s.id));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="oc-add-vendor-btn"
          className="w-full rounded-xl border-dashed border-border font-body text-sm text-foreground/70 gap-2 justify-center hover:border-teal hover:text-teal"
        >
          <Plus className="h-4 w-4" /> Add vendor
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Search vendors..." data-testid="oc-vendor-search" />
          <CommandList>
            <CommandEmpty>No vendors found.</CommandEmpty>
            <CommandGroup>
              {available.map(s => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  data-testid={`oc-vendor-option-${s.id}`}
                  onSelect={() => { onAdd(s.id); setOpen(false); }}
                  className="font-body text-sm cursor-pointer"
                >
                  {s.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function MonthCard({ month, suppliers, supMap, onSave }) {
  const [ids, setIds] = useState(month.supplier_ids || []);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(ids) !== JSON.stringify(month.supplier_ids || []);

  const add = (id) => setIds(prev => [...prev, id]);
  const remove = (id) => setIds(prev => prev.filter(x => x !== id));
  const move = (i, dir) => setIds(prev => {
    const arr = [...prev];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const save = async () => {
    setSaving(true);
    try {
      await onSave(month.month_key, ids);
      toast.success(`${monthLabel(month)} saved`);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden flex flex-col" data-testid={`oc-month-${month.month_key}`}>
      <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
        <h3 className="font-heading text-base font-semibold tracking-wide text-foreground">{monthLabel(month)}</h3>
        <span className="font-body text-[11px] text-foreground/50 bg-card px-2 py-0.5 rounded-full border border-border" data-testid={`oc-count-${month.month_key}`}>
          {ids.length}
        </span>
      </div>

      <div className="p-3 space-y-1.5 flex-1">
        {ids.length === 0 && (
          <p className="font-body text-xs text-foreground/40 py-3 text-center" data-testid={`oc-empty-${month.month_key}`}>
            No vendors scheduled.
          </p>
        )}
        {ids.map((id, i) => (
          <div key={id} className="flex items-center gap-2 rounded-lg bg-muted px-2.5 py-1.5 group" data-testid={`oc-row-${month.month_key}-${id}`}>
            <div className="flex flex-col -my-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-foreground/30 hover:text-teal disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`oc-up-${month.month_key}-${id}`}>
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === ids.length - 1} className="text-foreground/30 hover:text-teal disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`oc-down-${month.month_key}-${id}`}>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <span className="font-body text-sm text-foreground flex-1 truncate">{supMap[id] || 'Unknown vendor'}</span>
            <button onClick={() => remove(id)} className="text-foreground/30 hover:text-red-500" data-testid={`oc-remove-${month.month_key}-${id}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="pt-1.5">
          <VendorPicker suppliers={suppliers} selectedIds={ids} onAdd={add} />
        </div>
      </div>

      <div className="px-3 pb-3">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`oc-save-${month.month_key}`}
          className="w-full rounded-xl bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold gap-2 disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : dirty ? <Save className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          {dirty ? 'Save' : 'Saved'}
        </Button>
      </div>
    </div>
  );
}

export default function AdminOrderCycle() {
  const [months, setMonths] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => {
    orderCycleAPI.adminGet().then(r => setMonths(r.data.months)).catch(() => toast.error('Failed to load calendar'));
    adminAPI.listSuppliers()
      .then(r => setSuppliers([...r.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))))
      .catch(() => {});
  };
  useEffect(load, []);

  const supMap = Object.fromEntries(suppliers.map(s => [s.id, s.name]));

  const handleSave = async (monthKey, ids) => {
    await orderCycleAPI.adminSet(monthKey, ids);
    setMonths(prev => prev.map(m => m.month_key === monthKey ? { ...m, supplier_ids: ids } : m));
  };

  if (!months) return (
    <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
  );

  return (
    <div data-testid="admin-order-cycle-page" className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-teal/10"><CalendarRange className="h-5 w-5 text-teal" /></div>
          <div>
            <h1 className="font-heading text-2xl font-medium text-foreground">Order Cycle Calendar</h1>
            <p className="font-body text-xs text-foreground/60">Plan which vendors take orders over the next six months. Shown read-only on the homepage.</p>
          </div>
        </div>
        <Link to="/" target="_blank">
          <Button variant="outline" className="rounded-full font-body text-sm gap-2" data-testid="oc-preview-btn">
            <Eye className="h-4 w-4" /> Preview
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {months.map(m => (
          <MonthCard key={m.month_key} month={m} suppliers={suppliers} supMap={supMap} onSave={handleSave} />
        ))}
      </div>
    </div>
  );
}
